import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../../config/api';
import logo from '../../assets/logo.png';
import './WaitingRoomScreen.css';

export default function WaitingRoomScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    sessionCode,
    mode,
    isCreator,
    partnerAutoAdded,
  } = location.state || {};

  const [participants, setParticipants] = useState([]);
  const [isReady, setIsReady] = useState(false);
  const [allReady, setAllReady] = useState(false);
  const [starting, setStarting] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState('');
  const [liveProgress, setLiveProgress] = useState({});
  const [waitSeconds, setWaitSeconds] = useState(0);
  const [showSoloFallback, setShowSoloFallback] = useState(false);
  const [convertingToSolo, setConvertingToSolo] = useState(false);
  const [reminderSent, setReminderSent] = useState(false);

  const pollSession = useCallback(async () => {
    if (!sessionCode) return;
    try {
      const { data } = await api.get(`/sessions/${sessionCode}`);
      const session = data.session || data;

      if (session.status === 'active') {
        setAllReady(true);
        navigate(`/session/${sessionCode}`, { replace: true });
        return;
      }

      if (session.status === 'completed') {
        navigate(`/results/${sessionCode}`, { replace: true });
        return;
      }

      const parts = session.participants || [];
      setParticipants(parts);
      const everyoneReady = parts.length > 0 && parts.every(p => p.isReady);
      setAllReady(everyoneReady);

      // Fetch live progress if session is active
      if (session.status === 'active') {
        try {
          const { data: progressData } = await api.get(`/sessions/${sessionCode}/progress`);
          setLiveProgress(progressData.progress || {});
        } catch (err) {
          // Non-critical, continue
        }
      }
    } catch (err) {
      console.error('Poll error:', err);
    }
  }, [sessionCode, navigate]);

  useEffect(() => {
    if (!sessionCode) {
      navigate('/home', { replace: true });
      return;
    }
    pollSession();
    const interval = setInterval(pollSession, 3000);
    return () => clearInterval(interval);
  }, [sessionCode, navigate, pollSession]);

  // Auto-fallback timer: show "Continue Solo" after 45 seconds if partner hasn't joined
  useEffect(() => {
    if (!isCreator || mode === 'solo') return;
    const timer = setInterval(() => {
      setWaitSeconds(prev => {
        const next = prev + 1;
        if (next >= 45 && participants.length < 2) {
          setShowSoloFallback(true);
        }
        return next;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [isCreator, mode, participants.length]);

  async function handleContinueSolo() {
    setConvertingToSolo(true);
    try {
      // Force-start the session so it becomes active, then navigate to swipe screen
      await api.post(`/sessions/${sessionCode}/force-start`);
      navigate(`/session/${sessionCode}`, { replace: true });
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to start solo');
      setConvertingToSolo(false);
    }
  }

  async function handleReady() {
    try {
      const { data } = await api.post(`/sessions/${sessionCode}/ready`);
      setIsReady(true);
      if (data.allReady) {
        setAllReady(true);
        if (data.status === 'active') {
          navigate(`/session/${sessionCode}`, { replace: true });
        }
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to mark ready');
    }
  }

  async function handleForceStart() {
    setStarting(true);
    try {
      const { data } = await api.post(`/sessions/${sessionCode}/force-start`);
      if (data.status === 'active') {
        navigate(`/session/${sessionCode}`, { replace: true });
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to start session');
      setStarting(false);
    }
  }

  function copyCode() {
    navigator.clipboard.writeText(sessionCode).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }).catch(() => {
      // Fallback
      const el = document.createElement('textarea');
      el.value = sessionCode;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  function getJoinLink() {
    return `${window.location.origin}/join/${sessionCode}`;
  }

  function shareSession() {
    const joinLink = getJoinLink();
    const text = isCoupleMode
      ? `Pick dinner with me on Hangrily 💕 Tap to join: ${joinLink}`
      : `Join my Hangrily session! Tap to join: ${joinLink}`;
    if (navigator.share) {
      navigator.share({ title: 'Hangrily Session', text, url: joinLink });
    } else {
      navigator.clipboard.writeText(text).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }).catch(() => copyCode());
    }
  }

  function remindPartner() {
    setReminderSent(true);
    shareSession();
    setTimeout(() => setReminderSent(false), 4000);
  }

  const isCoupleMode = mode === 'couple';
  const minPlayers = isCoupleMode ? 2 : 2;
  const canForceStart = isCreator && participants.length >= minPlayers && !allReady;

  return (
    <div className="waiting-room-screen">
      <header className="waiting-header">
        <img src={logo} alt="Hangrily" onClick={() => navigate('/')} style={{ height: '26px', cursor: 'pointer' }} />
        <h1 className="waiting-title">
          {isCoupleMode ? 'Couple Session' : 'Group Session'}
        </h1>
        <div style={{ width: '60px' }}></div>
      </header>

      <main className="waiting-main">
        <div className="waiting-container">
          {/* Session Code */}
          <div className="code-section">
            <p className="code-label">Session Code</p>
            <div className="code-display" onClick={copyCode}>
              <h2 className="session-code-large">{sessionCode}</h2>
              <span className="copy-hint">{copied ? '✓ Copied!' : 'Tap to copy'}</span>
            </div>
            <button className="share-btn" onClick={shareSession}>
              📤 Share Invite Link
            </button>
          </div>

          {/* Participants */}
          <div className="participants-section">
            <h3 className="section-label">
              Players ({participants.length})
            </h3>
            <div className="participants-list">
              {participants.map((p, i) => {
                const progress = liveProgress[p.userId] || {};
                const swipeCount = progress.swipeCount || 0;
                const totalRestaurants = progress.totalRestaurants || 0;
                const showProgress = swipeCount > 0 && totalRestaurants > 0;
                
                return (
                  <div key={i} className={`participant-card ${p.isReady ? 'ready' : ''}`}>
                    <div className="participant-avatar">
                      {p.isCreator ? '👑' : '👤'}
                    </div>
                    <div className="participant-info">
                      <span className="participant-name">{p.displayName}</span>
                      <span className={`participant-status ${p.isReady ? 'ready' : 'waiting'}`}>
                        {p.isReady ? '✓ Ready' : 'Waiting...'}
                      </span>
                      {showProgress && (
                        <div className="live-progress-bar">
                          <div 
                            className="progress-fill-live"
                            style={{ width: `${(swipeCount / totalRestaurants) * 100}%` }}
                          />
                          <span className="progress-text">
                            {swipeCount}/{totalRestaurants} swiped
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}

              {isCoupleMode && participants.length < 2 && (
                <div className="participant-card placeholder">
                  <div className="participant-avatar">⏳</div>
                  <div className="participant-info">
                    <span className="participant-name">Waiting for partner...</span>
                    <span className="participant-status waiting">
                      {partnerAutoAdded ? 'Invited' : 'Share the link'}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Solo fallback prompt */}
          {showSoloFallback && participants.length < 2 && (
            <div className="solo-fallback-section" style={{
              background: 'linear-gradient(135deg, #2a1a3a, #1a2a3a)',
              border: '1px solid #FF6347',
              borderRadius: '14px',
              padding: '16px',
              marginBottom: '16px',
              textAlign: 'center',
            }}>
              <p style={{ color: '#fff', fontSize: '15px', margin: '0 0 8px' }}>
                ⏱️ Partner hasn't joined yet
              </p>
              <p style={{ color: '#aaa', fontSize: '13px', margin: '0 0 12px' }}>
                No worries — you can start swiping on your own!
              </p>
              {isCoupleMode && (
                <button
                  className="btn btn-secondary btn-lg"
                  onClick={remindPartner}
                  style={{ width: '100%', marginBottom: '10px' }}
                >
                  {reminderSent ? '✓ Reminder sent!' : '🔔 Remind Partner'}
                </button>
              )}
              <button
                className="btn btn-primary btn-lg"
                onClick={handleContinueSolo}
                disabled={convertingToSolo}
                style={{ width: '100%' }}
              >
                {convertingToSolo ? 'Starting...' : '🍽️ Continue Solo'}
              </button>
            </div>
          )}

          {/* Status Message */}
          <div className="status-section">
            {isCoupleMode && participants.length < 2 && !showSoloFallback && (
              <p className="status-message">
                💕 Waiting for your partner to join and set their preferences...
              </p>
            )}
            {isCoupleMode && participants.length < 2 && showSoloFallback && (
              <p className="status-message" style={{ fontSize: '12px', color: '#888' }}>
                Still waiting... or tap above to swipe solo
              </p>
            )}
            {participants.length >= minPlayers && !allReady && !isReady && (
              <p className="status-message">
                Everyone's here! Tap Ready when you want to start swiping.
              </p>
            )}
            {isReady && !allReady && (
              <p className="status-message">
                ⏳ You're ready! Waiting for others...
              </p>
            )}
          </div>

          {error && <div className="waiting-error">{error}</div>}

          {/* Actions */}
          <div className="waiting-actions">
            {!isReady && participants.length >= minPlayers && (
              <button className="btn btn-primary btn-lg" onClick={handleReady}>
                ✓ I'm Ready!
              </button>
            )}

            {canForceStart && (
              <button
                className="btn btn-secondary btn-lg"
                onClick={handleForceStart}
                disabled={starting}
              >
                {starting ? 'Starting...' : '⚡ Start Now'}
              </button>
            )}
          </div>

          {/* Couple mode info */}
          {isCoupleMode && (
            <div className="info-card">
              <h4>💑 How Couple Mode Works</h4>
              <ol>
                <li>Both partners set their preferences</li>
                <li>Restaurants are merged from both preferences</li>
                <li>You each swipe independently</li>
                <li>Restaurants you BOTH liked win!</li>
              </ol>
            </div>
          )}

          {/* Group mode info */}
          {mode === 'group' && (
            <div className="info-card">
              <h4>👥 How Group Mode Works</h4>
              <ol>
                <li>Share the session code with your group</li>
                <li>Everyone joins and marks ready</li>
                <li>Everyone swipes through the same restaurants</li>
                <li>The restaurant with the most votes wins!</li>
              </ol>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
